'use client';

import { TRACKED_EVENT_NAME, TrackedEventProps } from '@/constants';
import { isOwnDomain, validateGtag } from '@/utils';
import { useLocale } from 'next-intl';
import { LinkProps } from 'next/link';
import { Link } from '@/navigation';
import React, { AnchorHTMLAttributes, useCallback } from 'react';

export interface TrackedAnchorProps
  extends
    Omit<LinkProps, 'prefetch'>,
    AnchorHTMLAttributes<HTMLAnchorElement>,
    TrackedEventProps {
  href: string;
  children: React.ReactNode;
  className?: string;
  prefetch?: boolean | 'auto' | null;
  eventName?: (typeof TRACKED_EVENT_NAME)[keyof typeof TRACKED_EVENT_NAME];
  gtagOptions?: Record<string, any>;
}

const getReferrerValue = (href: string) => {
  // relative path / fragment 는 same-origin → rel 무의미. new URL() 의 throw 를
  // 정상 흐름으로 쓰지 않기 위해 early return.
  if (!href || /^[/#.]/.test(href)) return undefined;
  try {
    const url = new URL(href);
    return isOwnDomain(url.hostname) ? undefined : 'noopener noreferrer';
  } catch (error) {
    return 'noopener noreferrer';
  }
};

export function TrackedAnchor({
  href,
  children,
  label,
  position,
  prefetch,
  onClick,
  eventName = TRACKED_EVENT_NAME.CLICK_LINK,
  locale: _locale,
  gtagOptions,
  ...props
}: TrackedAnchorProps) {
  const isExternal = href.startsWith('http');

  const locale = useLocale();

  const baseProps = isExternal
    ? {
        target: '_blank',
        rel: getReferrerValue(href),
        ...props,
      }
    : {
        prefetch: prefetch === null ? undefined : prefetch,
        ...props,
      };

  const onTrackClick = useCallback(() => {
    if (!validateGtag()) return;

    window.gtag('event', eventName, {
      event_label: label,
      link_url: href,
      position,
      user_language: locale,
      ...gtagOptions,
    });
  }, [eventName, label, href, position, locale, gtagOptions]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      onTrackClick();
      onClick?.(e);
    },
    [onTrackClick, onClick],
  );

  return (
    <Link
      href={href}
      onClick={handleClick}
      // 프리렌더된 gene 정적 페이지의 위임 리스너(scripts/pack-gene-pages.ts 의
      // TRACKING_SNIPPET)가 이 속성들을 읽어 동일한 gtag 이벤트를 발화한다.
      // 하이드레이션된 페이지에는 리스너가 없어 이중 발화되지 않는다.
      data-gtag-event={eventName}
      data-gtag-label={label}
      data-gtag-position={position}
      data-gtag-extra={gtagOptions ? JSON.stringify(gtagOptions) : undefined}
      {...baseProps}
    >
      {children}
    </Link>
  );
}
